/**
 * render/momentum_ui.ts — The Flow State Made Visible
 *
 * The momentum ODE in combat/dynamics.ts is a beautiful dynamical system
 * that, until now, has been computed in silence. This module renders it
 * as a living circular meter beside the player's combat panel:
 *
 *   - A continuous arc fill [0,1] colored by a blue → gold → hot-orange
 *     gradient, reflecting the current momentum value.
 *   - A dim fatigue ceiling band that grows as sustained combat erodes
 *     the usable ceiling of the meter.
 *   - A gold notch at the 0.65 flow threshold.
 *   - A bright high-water tick that marks the peak momentum achieved
 *     this engagement.
 *   - An inner heartbeat trace plotting the last ~3 seconds of values,
 *     so players can SEE the shape of their own dynamical system: the
 *     rising slope after a crit, the quadratic fall when they're hit,
 *     the exponential decay between swings.
 *   - When momentum crosses the flow threshold, an outer pulsing aura
 *     ignites in warm gold. Flow state is no longer an invisible
 *     number — it is a radial halo that brightens the whole HUD.
 *   - The existing combo count is embedded in the meter's center so
 *     nothing is lost from the previous discrete readout.
 *
 * This file is a pure renderer: it owns its telemetry buffer and
 * pulls a read-only MomentumState snapshot from combat/system.ts via
 * the caller. Zero allocations in the hot path once the Float32Arrays
 * are created.
 */

import type { MomentumState } from "../combat/dynamics.js";

// ─── Telemetry ─────────────────────────────────────────────────────────────
// Rolling ring buffer of recent momentum samples. Sampled every draw call,
// drawn as an inner heartbeat trace.

const HISTORY_WINDOW = 3.0;       // seconds of momentum history to retain
const HISTORY_SAMPLE_HZ = 45;     // sample cap — matches render frame rate
const MAX_SAMPLES = Math.ceil(HISTORY_WINDOW * HISTORY_SAMPLE_HZ);

export interface MomentumTelemetry {
  /** Rolling values buffer */
  values: Float32Array;
  /** Matching timestamps (seconds) */
  timestamps: Float32Array;
  /** Write head for the ring buffer */
  head: number;
  /** Number of valid samples in the buffer (saturates at MAX_SAMPLES) */
  filled: number;
  /** Previous flow state (for edge detection) */
  prevInFlow: boolean;
  /** Timestamp of the last flow-enter transition (used by ignition flash) */
  lastFlowEnter: number;
  /** Rolling peak flash amount [0,1] — pops when a new peak is reached */
  peakFlash: number;
  /** Previous peak value (for edge detection of new peaks) */
  prevPeak: number;
  /** Previous value (for edge detection of impulse flashes) */
  prevValue: number;
  /** Hit impulse flash [0,1] — pops when momentum jumps sharply upward */
  hitFlash: number;
}

export function createMomentumTelemetry(): MomentumTelemetry {
  return {
    values: new Float32Array(MAX_SAMPLES),
    timestamps: new Float32Array(MAX_SAMPLES),
    head: 0,
    filled: 0,
    prevInFlow: false,
    lastFlowEnter: -Infinity,
    peakFlash: 0,
    prevPeak: 0,
    prevValue: 0,
    hitFlash: 0,
  };
}

/** Insert a new momentum sample into the ring buffer. */
function recordSample(tel: MomentumTelemetry, value: number, now: number): void {
  tel.values[tel.head] = value;
  tel.timestamps[tel.head] = now;
  tel.head = (tel.head + 1) % MAX_SAMPLES;
  if (tel.filled < MAX_SAMPLES) tel.filled++;
}

// ─── Palette ───────────────────────────────────────────────────────────────
// Matches the warm wood/gold palette used across combat_ui.ts.

const C = {
  ringBgDark:   "#0c0804",
  ringBgMid:    "#1e1508",
  ringBorder:   "#3a2810",
  goldDark:     "#6b4f0c",
  goldMid:      "#9b7318",
  goldBright:   "#d4a030",
  goldGlow:     "#f0c860",
  textPrime:    "#f0d88a",
  textDim:      "#c0a060",
  flowHot:      "#ffcc44",
  flowBlaze:    "#ffe890",
  fatigueGray:  "#2f261c",
  fatigueLine:  "#4a3c28",
  peakMark:     "#ffeeaa",
  traceBg:      "rgba(0,0,0,0.40)",
  traceLine:    "#f0c860",
  traceRef:     "rgba(255,204,68,0.35)",
} as const;

/**
 * Color for the momentum arc at a given value [0,1].
 * Cool blue (0) → gold (0.5) → hot orange-white (1).
 * Two-segment linear interpolation keeps it cheap and legible.
 */
function momentumColor(v: number): string {
  if (v < 0.5) {
    const t = v * 2;
    const r = Math.round(120 + (212 - 120) * t);
    const g = Math.round(160 + (160 - 160) * t);
    const b = Math.round(224 + (48 - 224) * t);
    return `rgb(${r},${g},${b})`;
  } else {
    const t = (v - 0.5) * 2;
    const r = Math.round(212 + (255 - 212) * t);
    const g = Math.round(160 + (200 - 160) * t);
    const b = Math.round(48 + (80 - 48) * t);
    return `rgb(${r},${g},${b})`;
  }
}

// ─── Main Draw ─────────────────────────────────────────────────────────────

export interface MomentumMeterParams {
  /** Meter center X (canvas pixels) */
  cx: number;
  /** Meter center Y (canvas pixels) */
  cy: number;
  /** Outer ring radius (pixels) — inner radius is outerR - 6 */
  outerR: number;
  /** Current combo count to render in the center (pass 0 to omit) */
  combo: number;
  /** Time since last frame in seconds — used for flash decay */
  dt: number;
  /** Current time in seconds (performance.now() / 1000) */
  now: number;
  /** True when combat is active — out of combat we fade the whole meter */
  active: boolean;
}

/**
 * Draw the momentum meter. Call every frame while the combat panel is
 * visible; the telemetry buffer samples from the passed momentum state.
 */
export function drawMomentumMeter(
  ctx: CanvasRenderingContext2D,
  momentum: Readonly<MomentumState>,
  tel: MomentumTelemetry,
  params: MomentumMeterParams,
): void {
  const { cx, cy, outerR, combo, dt, now, active } = params;
  const { value, fatigue, peak, inFlow } = momentum;

  // ── Sampling & edge detection ─────────────────────────────────────
  recordSample(tel, value, now);

  // Flow transition — ignition flash when crossing from false → true
  if (inFlow && !tel.prevInFlow) {
    tel.lastFlowEnter = now;
  }
  tel.prevInFlow = inFlow;

  // New peak flash
  if (peak > tel.prevPeak + 0.01) {
    tel.peakFlash = 1;
  }
  tel.prevPeak = peak;

  // Hit impulse flash — sharp upward jump
  const dValue = value - tel.prevValue;
  if (dValue > 0.04) {
    tel.hitFlash = Math.min(1, tel.hitFlash + dValue * 3.5);
  }
  tel.prevValue = value;

  // Decay flashes using delta time
  tel.peakFlash = Math.max(0, tel.peakFlash - dt * 1.4);
  tel.hitFlash = Math.max(0, tel.hitFlash - dt * 4.0);

  // Global meter alpha — fade when out of combat so the meter rests quietly
  const idleAlpha = active ? 1.0 : 0.55;

  ctx.save();
  ctx.globalAlpha = idleAlpha;
  ctx.translate(cx, cy);

  // ── Flow aura (rendered first, underneath everything) ─────────────
  if (inFlow) {
    const timeInFlow = now - tel.lastFlowEnter;
    const ignite = Math.min(1, timeInFlow * 1.8); // ease-in over ~0.5s
    const pulseT = Math.sin(now * Math.PI * 2.6) * 0.5 + 0.5;
    const auraR = outerR + 3 + pulseT * 4;
    const auraAlpha = (0.14 + pulseT * 0.16) * ignite;
    const auraG = ctx.createRadialGradient(0, 0, outerR * 0.8, 0, 0, auraR + 10);
    auraG.addColorStop(0, `rgba(255,204,68,${auraAlpha})`);
    auraG.addColorStop(0.6, `rgba(255,204,68,${auraAlpha * 0.4})`);
    auraG.addColorStop(1, "rgba(255,204,68,0)");
    ctx.fillStyle = auraG;
    ctx.beginPath();
    ctx.arc(0, 0, auraR + 10, 0, Math.PI * 2);
    ctx.fill();
  }

  // Hit impulse ring — quick outer ripple when the meter jumps
  if (tel.hitFlash > 0.01) {
    const ringR = outerR + 2 + (1 - tel.hitFlash) * 3;
    ctx.save();
    ctx.strokeStyle = `rgba(255,236,160,${tel.hitFlash * 0.7})`;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.arc(0, 0, ringR, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  // ── Background ring ───────────────────────────────────────────────
  const ringThickness = 6;
  const innerR = outerR - ringThickness;
  const midR = (outerR + innerR) * 0.5;

  // Outer filled circle (ring body)
  ctx.beginPath();
  ctx.arc(0, 0, outerR, 0, Math.PI * 2);
  ctx.fillStyle = C.ringBgMid;
  ctx.fill();

  // Inner recess — carves out the ring body leaving only the band
  ctx.beginPath();
  ctx.arc(0, 0, innerR, 0, Math.PI * 2);
  ctx.fillStyle = C.ringBgDark;
  ctx.fill();

  // ── Fatigue ceiling band ──────────────────────────────────────────
  // Fatigue erodes the top of the usable range. Show the unavailable
  // portion as a desaturated gray arc running backwards from full.
  const fatigueClamp = Math.max(0, Math.min(1, fatigue * 0.4));
  if (fatigueClamp > 0.01) {
    ctx.save();
    ctx.strokeStyle = C.fatigueGray;
    ctx.lineWidth = ringThickness;
    ctx.lineCap = "butt";
    const startA = -Math.PI / 2 + (1 - fatigueClamp) * Math.PI * 2;
    const endA = -Math.PI / 2 + Math.PI * 2;
    ctx.beginPath();
    ctx.arc(0, 0, midR, startA, endA);
    ctx.stroke();
    ctx.restore();
  }

  // ── Momentum arc fill ─────────────────────────────────────────────
  if (value > 0.001) {
    ctx.save();
    ctx.strokeStyle = momentumColor(value);
    ctx.lineWidth = ringThickness;
    ctx.lineCap = "round";
    if (inFlow) {
      ctx.shadowColor = C.flowHot;
      ctx.shadowBlur = 10;
    } else if (tel.hitFlash > 0.1) {
      ctx.shadowColor = C.goldGlow;
      ctx.shadowBlur = 6 * tel.hitFlash;
    }
    const startA = -Math.PI / 2;
    const endA = startA + value * Math.PI * 2;
    ctx.beginPath();
    ctx.arc(0, 0, midR, startA, endA);
    ctx.stroke();
    ctx.restore();
  }

  // ── Flow threshold notch at 0.65 ──────────────────────────────────
  {
    const flowA = -Math.PI / 2 + 0.65 * Math.PI * 2;
    const nx = Math.cos(flowA);
    const ny = Math.sin(flowA);
    ctx.save();
    ctx.strokeStyle = inFlow ? C.flowBlaze : C.goldGlow;
    ctx.lineWidth = 1.4;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(nx * (innerR - 1), ny * (innerR - 1));
    ctx.lineTo(nx * (outerR + 2), ny * (outerR + 2));
    ctx.stroke();
    ctx.restore();
  }

  // ── Peak high-water tick ──────────────────────────────────────────
  if (peak > 0.05) {
    const peakA = -Math.PI / 2 + peak * Math.PI * 2;
    const px = Math.cos(peakA);
    const py = Math.sin(peakA);
    const flashBoost = tel.peakFlash * 2.2;
    ctx.save();
    ctx.fillStyle = C.peakMark;
    if (tel.peakFlash > 0.01) {
      ctx.shadowColor = C.flowBlaze;
      ctx.shadowBlur = 6 + flashBoost * 4;
    }
    ctx.beginPath();
    ctx.arc(px * midR, py * midR, 1.8 + flashBoost * 0.8, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // ── Ring borders for definition ───────────────────────────────────
  ctx.beginPath();
  ctx.arc(0, 0, outerR, 0, Math.PI * 2);
  ctx.strokeStyle = C.ringBorder;
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(0, 0, innerR, 0, Math.PI * 2);
  ctx.strokeStyle = C.ringBorder;
  ctx.lineWidth = 0.5;
  ctx.stroke();

  // ── Inner heartbeat trace ─────────────────────────────────────────
  drawHeartbeat(ctx, tel, innerR - 3, now, combo);

  // ── Combo number in center ────────────────────────────────────────
  if (combo > 0) {
    const pulseAmp = Math.min(0.15, 0.05 + combo * 0.01);
    const pulse = 1 + Math.sin(now * 12) * pulseAmp;
    ctx.save();
    ctx.scale(pulse, pulse);
    const fontSize = Math.min(18, 11 + Math.floor(combo / 3));
    ctx.font = `bold ${fontSize}px monospace`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.strokeStyle = "rgba(0,0,0,0.85)";
    ctx.lineWidth = 3;
    ctx.lineJoin = "round";
    const cy2 = combo > 0 ? -5 : 0;
    ctx.strokeText(`x${combo}`, 0, cy2);
    const col = combo >= 6 ? "#ff8844" : combo >= 3 ? "#e87820" : C.goldBright;
    ctx.fillStyle = col;
    if (combo >= 3) {
      ctx.shadowColor = col;
      ctx.shadowBlur = 6;
    }
    ctx.fillText(`x${combo}`, 0, cy2);
    ctx.restore();
  }

  // ── Label below ring ──────────────────────────────────────────────
  ctx.save();
  ctx.font = "bold 7px monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  if (inFlow) {
    // Blazing "FLOW" label
    ctx.shadowColor = C.flowHot;
    ctx.shadowBlur = 6;
    ctx.fillStyle = C.flowBlaze;
    ctx.fillText("◆ FLOW ◆", 0, outerR + 4);
  } else {
    ctx.fillStyle = C.textDim;
    ctx.fillText("MOMENTUM", 0, outerR + 4);
  }
  ctx.restore();

  ctx.restore();
}

/**
 * Plot the last HISTORY_WINDOW seconds of momentum values inside the
 * lower half of the meter's inner area. A tiny heartbeat trace that
 * shows the actual shape of the ODE playing out in real time.
 */
function drawHeartbeat(
  ctx: CanvasRenderingContext2D,
  tel: MomentumTelemetry,
  innerR: number,
  now: number,
  combo: number,
): void {
  if (tel.filled < 2) return;

  const w = innerR * 1.7;
  const h = innerR * 0.55;
  const x0 = -w * 0.5;
  // When a combo is present, the combo number occupies the upper half,
  // so the heartbeat lives in the lower half. With no combo, center it.
  const y0 = combo > 0 ? 3 : -h * 0.5;

  ctx.save();

  // Background panel inset with rounded ends
  ctx.fillStyle = C.traceBg;
  const r = 1.5;
  ctx.beginPath();
  ctx.moveTo(x0 + r, y0);
  ctx.lineTo(x0 + w - r, y0);
  ctx.arcTo(x0 + w, y0, x0 + w, y0 + r, r);
  ctx.lineTo(x0 + w, y0 + h - r);
  ctx.arcTo(x0 + w, y0 + h, x0 + w - r, y0 + h, r);
  ctx.lineTo(x0 + r, y0 + h);
  ctx.arcTo(x0, y0 + h, x0, y0 + h - r, r);
  ctx.lineTo(x0, y0 + r);
  ctx.arcTo(x0, y0, x0 + r, y0, r);
  ctx.closePath();
  ctx.fill();

  // Flow threshold reference line (dashed)
  const thy = y0 + h - 0.65 * h;
  ctx.setLineDash([1, 2]);
  ctx.strokeStyle = C.traceRef;
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(x0, thy);
  ctx.lineTo(x0 + w, thy);
  ctx.stroke();
  ctx.setLineDash([]);

  // Plot the line
  ctx.beginPath();
  const n = tel.filled;
  let firstDrawn = true;
  let lastPX = 0;
  let lastPY = 0;
  for (let i = 0; i < n; i++) {
    const idx = (tel.head - n + i + MAX_SAMPLES) % MAX_SAMPLES;
    const t = tel.timestamps[idx];
    const age = now - t;
    if (age > HISTORY_WINDOW) continue;
    const tNorm = 1 - age / HISTORY_WINDOW;
    const px = x0 + tNorm * w;
    const py = y0 + h - tel.values[idx] * h;
    if (firstDrawn) {
      ctx.moveTo(px, py);
      firstDrawn = false;
    } else {
      ctx.lineTo(px, py);
    }
    lastPX = px;
    lastPY = py;
  }
  ctx.strokeStyle = C.traceLine;
  ctx.lineWidth = 1;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.stroke();

  // Leading dot at the newest sample
  if (!firstDrawn) {
    ctx.fillStyle = C.goldGlow;
    ctx.beginPath();
    ctx.arc(lastPX, lastPY, 1.2, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}
